<script setup lang="ts">
import { PenLine, BarChart3, Table } from 'lucide-vue-next';
import SceneShell from '../SceneShell.vue';
</script>

<template>
    <SceneShell>
        <div
            class="pointer-events-none absolute -top-16 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-cyan-400/10 blur-3xl"
        ></div>

        <!-- Flying-in PRISMA chip -->
        <div class="chip chip-prisma" aria-hidden="true">
            <BarChart3 class="h-3.5 w-3.5" />
            PRISMA
        </div>
        <!-- Flying-in grid chip -->
        <div class="chip chip-grid" aria-hidden="true">
            <Table class="h-3.5 w-3.5" />
            Grille
        </div>

        <!-- Editor page -->
        <div class="page" aria-hidden="true">
            <div class="page-head">
                <PenLine class="h-3.5 w-3.5 text-cyan-300" />
                <span class="dot"></span>
                <span class="dot"></span>
            </div>

            <span class="line type-1"></span>
            <span class="line type-2"></span>

            <!-- inserted PRISMA block -->
            <div class="block-prisma block">
                <BarChart3 class="h-4 w-4" />
                <span class="block-bars"><i></i><i></i><i></i></span>
            </div>

            <span class="line type-3"></span>

            <!-- inserted grid block -->
            <div class="block-grid block">
                <Table class="h-4 w-4" />
                <span class="block-grid-cells"
                    ><i></i><i></i><i></i><i></i><i></i><i></i
                ></span>
            </div>

            <span class="line type-4 w-1/2"></span>
        </div>

        <p class="caption">
            Rédigez et insérez diagramme &amp; grille, au même endroit.
        </p>
    </SceneShell>
</template>

<style scoped>
.page {
    position: absolute;
    top: 47%;
    left: 50%;
    z-index: 10;
    display: flex;
    width: clamp(150px, 46%, 230px);
    transform: translate(-50%, -50%);
    flex-direction: column;
    gap: 7px;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.25);
    background: rgba(15, 23, 42, 0.85);
    padding: 12px;
}

.page-head {
    display: flex;
    align-items: center;
    gap: 5px;
    padding-bottom: 4px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.dot {
    height: 5px;
    width: 5px;
    border-radius: 9999px;
    background: rgba(148, 163, 184, 0.4);
}

.line {
    height: 4px;
    width: 100%;
    border-radius: 9999px;
    background: rgba(148, 163, 184, 0.4);
    transform-origin: left;
}

.block {
    display: flex;
    align-items: center;
    gap: 8px;
    border-radius: 6px;
    border: 1px solid rgba(34, 211, 238, 0.25);
    background: rgba(34, 211, 238, 0.06);
    padding: 7px 9px;
    color: rgb(103 232 249);
}

.block-bars {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 16px;
}
.block-bars i {
    width: 5px;
    border-radius: 1px;
    background: rgba(34, 211, 238, 0.6);
}
.block-bars i:nth-child(1) {
    height: 60%;
}
.block-bars i:nth-child(2) {
    height: 100%;
}
.block-bars i:nth-child(3) {
    height: 40%;
}

.block-grid-cells {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
}
.block-grid-cells i {
    height: 5px;
    width: 8px;
    border-radius: 1px;
    background: rgba(34, 211, 238, 0.45);
}

.chip {
    position: absolute;
    top: 50%;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 9999px;
    border: 1px solid rgba(34, 211, 238, 0.45);
    background: rgba(34, 211, 238, 0.12);
    padding: 3px 9px;
    font-size: 10px;
    font-weight: 600;
    color: rgb(103 232 249);
    transform: translate(-50%, -50%);
    opacity: 0;
}

.caption {
    position: absolute;
    bottom: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: rgb(100 116 139);
}

@media (prefers-reduced-motion: no-preference) {
    .type-1 {
        animation: type 5s ease-in-out infinite;
    }
    .type-2 {
        animation: type 5s ease-in-out infinite;
        animation-delay: 0.4s;
    }
    .type-3 {
        animation: type 5s ease-in-out infinite;
        animation-delay: 2.4s;
    }
    .type-4 {
        animation: type 5s ease-in-out infinite;
        animation-delay: 3.6s;
    }

    .block-prisma {
        animation: block-in 5s ease-in-out infinite;
        animation-delay: 1.4s;
    }
    .block-grid {
        animation: block-in 5s ease-in-out infinite;
        animation-delay: 3s;
    }

    .chip-prisma {
        left: 50%;
        animation: chip-left 5s ease-in-out infinite;
        animation-delay: 1s;
    }
    .chip-grid {
        left: 50%;
        animation: chip-right 5s ease-in-out infinite;
        animation-delay: 2.6s;
    }

    @keyframes type {
        0% {
            transform: scaleX(0);
            opacity: 0.4;
        }
        18%,
        100% {
            transform: scaleX(1);
            opacity: 1;
        }
    }

    @keyframes block-in {
        0% {
            opacity: 0;
            transform: translateY(6px) scale(0.95);
        }
        14%,
        100% {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    @keyframes chip-left {
        0% {
            left: 14%;
            opacity: 0;
        }
        20% {
            opacity: 1;
        }
        55% {
            left: 42%;
            opacity: 0;
        }
        100% {
            left: 42%;
            opacity: 0;
        }
    }

    @keyframes chip-right {
        0% {
            left: 86%;
            opacity: 0;
        }
        20% {
            opacity: 1;
        }
        55% {
            left: 58%;
            opacity: 0;
        }
        100% {
            left: 58%;
            opacity: 0;
        }
    }
}
</style>
